import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getTeacherGrades,
  createTeacherGrade,
  updateTeacherGrade,
  deleteTeacherGrade,
  getTeacherGradeById
} from '../services/teacherGradeService';
import Notification from '../components/Common/Notification';

export const TeacherGradeContext = createContext();

export const TeacherGradeProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [teacherGrades, setTeacherGrades] = useState([]);
  const [selectedTeacherGrade, setSelectedTeacherGrade] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllTeacherGrades = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherGrades(authHeaders);
      setTeacherGrades(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching teacher grades.';
      showNotification(errorMessage, true);
    }
  };

  const fetchTeacherGradeById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getTeacherGradeById(id, authHeaders);
      setSelectedTeacherGrade(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching teacher grade with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addTeacherGrade = async (teacherGradeData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newTeacherGrade = await createTeacherGrade(teacherGradeData, authHeaders);
      setTeacherGrades(prevTeacherGrades => [...prevTeacherGrades, newTeacherGrade]);
      showNotification('Teacher grade added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding teacher grade.';
      showNotification(errorMessage, true);
    }
  };

  const editTeacherGrade = async (id, teacherGradeData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedTeacherGrade = await updateTeacherGrade(id, teacherGradeData, authHeaders);
      setTeacherGrades(prevTeacherGrades =>
        prevTeacherGrades.map(tg => (tg.id === id ? updatedTeacherGrade : tg))
      );
      showNotification('Teacher grade updated successfully!', false);
      return updatedTeacherGrade;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating teacher grade.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeTeacherGrade = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteTeacherGrade(id, authHeaders);
      setTeacherGrades(prevTeacherGrades => prevTeacherGrades.filter(tg => tg.id !== id));
      showNotification('Teacher grade removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing teacher grade.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllTeacherGrades();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <TeacherGradeContext.Provider
      value={{
        teacherGrades,
        selectedTeacherGrade,
        fetchAllTeacherGrades,
        fetchTeacherGradeById,
        addTeacherGrade,
        editTeacherGrade,
        removeTeacherGrade,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </TeacherGradeContext.Provider>
  );
};
